import useTitanApp from "@/hooks/useTitanApp";
import { sendPatientQuestionnaires } from "@/dispatchers/patientDispatchers";
import { useState, useEffect } from "react";
import {
  setConsultationId,
  setFreeTypeMode,
  setIsFinalSummary,
  setPatientNotes,
  setSignatureDetails,
  setTranscript,
  setSelectedPatientConsultation,
  setisHandleStartConsultation,
  startConsultationFromQuestionnaire,
  getPatientConsultationByConsultationId,
  getPatientQuestionnairesByPatientyId,
  getPreConsultationByConsultationId,
  setPatientTabSwitcher,
  setPreConsultationTemplate,
  setPreConsultationTranscription,
  setPreConsult,
} from "@/dispatchers/patientDispatchers";
import {
  setPatientInfoTab,
  setConsultationLoadError,
} from "@/dispatchers/appDispatchers";
import { setHydratedTemplateSections } from "@/dispatchers/templateDispatchers";
import { useNavigate } from "react-router";
import { v4 as uuidv4 } from "uuid";
import { setShowNHS111ReferralTab } from "@/dispatchers/pharmacyServiceDispatchers";

export interface Questionnaire {
  id: string;
  name: string;
  completedDate: string;
  completedBy: string;
  status: "completed" | "pending" | "incomplete";
  score?: string;
  category: string;
  responses?: {
    question: string;
    answer: string;
  }[];
  notes?: string;
}

export const questionnaireCategories = [
  "General Health",
  "Travel Health",
  "Screening",
  "Mental Health",
  "Specialty",
  "Follow-up",
] as const;

export const questionnaireStatuses = [
  { value: "completed", label: "Completed", color: "green" },
  { value: "pending", label: "Pending", color: "orange" },
  { value: "incomplete", label: "Incomplete", color: "red" },
] as const;

export function usePatienQuestionnaireData() {
  const { patientState, appState } = useTitanApp();
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);

  const { patientQuestionnaires, selectedPatientDetails, consultationData } =
    patientState || {};
  const { currentPharmacy, currentPharmacyId, companyId } = appState || {};
  const navigate = useNavigate();

  const startNewConsultation = async (questionnaireId) => {
    setFreeTypeMode(false);
    setTranscript("");
    setPatientNotes("");
    setHydratedTemplateSections(null);
    setSignatureDetails(null);
    setSelectedPatientConsultation(null);

    const newConsultationId = uuidv4();
    setConsultationId(newConsultationId);
    setisHandleStartConsultation(true);
    setIsFinalSummary(true);

    try {
      const consultationDataPayload = {
        consultationId: newConsultationId,
        questionnaireId: questionnaireId,
        patientId: selectedPatientDetails?.patientID,
        companyID: companyId,
      };

      const consultation = await startConsultationFromQuestionnaire(
        consultationDataPayload,
      );

      if (!consultation) return;

      setSelectedPatientConsultation(consultation);
      setPatientInfoTab(1);

      setPreConsult(consultation);
      if (consultation?.preConsultationTemplate) {
        setPreConsultationTemplate(consultation.preConsultationTemplate);

        let preConsultTranscription = "";

        consultation.preConsultationTemplate.templateSections?.forEach(
          (section) => {
            section?.userControls?.forEach((control) => {
              if (control?.label && control?.value) {
                preConsultTranscription += ` ${control.label} ${control.value} `;
              }
            });
          },
        );

        setPreConsultationTranscription(preConsultTranscription.trim());
      } else {
        setPreConsultationTemplate(null);
        setPreConsultationTranscription("");
      }

      const hasPreConsult =
        !!consultation?.preConsultationTemplate?.templateSections?.length;

      setPatientTabSwitcher(hasPreConsult ? 1 : 0);
      setShowNHS111ReferralTab(false);
      navigate(
        `/pharmacy/patientconsultation/${currentPharmacy.teamPharmacyID}/${selectedPatientDetails?.patientID}/${newConsultationId}`,
      );
    } catch (error) {
      setConsultationLoadError(true);
      console.error("Failed to save consultation:", error);
    }
  };

  useEffect(() => {
    if (patientQuestionnaires && Array.isArray(patientQuestionnaires)) {
      const mappedQuestionnaires: Questionnaire[] = patientQuestionnaires.map(
        (item: any) => {
          const responses: { question: string; answer: string }[] = [];

          if (item.preConsultationTemplateSection?.templateSections) {
            item.preConsultationTemplateSection.templateSections.forEach(
              (section: any) => {
                if (
                  section.userControls &&
                  Array.isArray(section.userControls)
                ) {
                  section.userControls.forEach((control: any) => {
                    responses.push({
                      question: control.label || "Unlabeled question",
                      answer:
                        control.value !== null && control.value !== undefined
                          ? String(control.value)
                          : "Not answered",
                    });
                  });
                }
              },
            );
          }

          const getStatus = (status: number): "completed" | "pending" => {
            switch (status) {
              case 1:
                return "pending";
              case 2:
                return "completed";
              default:
                return "pending";
            }
          };

          const getCategory = (templateName: string): string => {
            const name = templateName?.toLowerCase() || "";
            if (name.includes("diabetes")) return "Specialty";
            if (name.includes("mental")) return "Mental Health";
            if (name.includes("travel")) return "Travel Health";
            if (name.includes("screening")) return "Screening";
            if (name.includes("follow")) return "Follow-up";
            return "General Health";
          };

          const formatDate = (dateString: string): string => {
            if (!dateString) return "";

            try {
              const date = new Date(dateString);
              if (isNaN(date.getTime())) {
                return "";
              }
              return date.toISOString().split("T")[0];
            } catch (error) {
              console.error("Error formatting date:", error);
              return "";
            }
          };

          return {
            id: item._id || "",
            name:
              item.preConsultationTemplateSection?.templateName ||
              "Unnamed Questionnaire",
            completedDate:
              formatDate(item.createdOn) ||
              formatDate(item.preConsultationTemplateSection?.updatedOn) ||
              "",
            completedBy:
              item.preConsultationTemplateSection?.updatedByName ||
              item.patient?.name ||
              "Unknown",
            status: getStatus(item.status),
            category: getCategory(
              item.preConsultationTemplateSection?.templateName,
            ),
            responses: responses.length > 0 ? responses : undefined,
            notes:
              item.preConsultationTemplateSection?.consultationSummary ||
              undefined,
          };
        },
      );

      setQuestionnaires(mappedQuestionnaires);
    } else {
      setQuestionnaires([]);
    }
  }, [patientQuestionnaires]);

  return {
    questionnaires,
    sendPatientQuestionnaires,
    patientQuestionnaires,
    setQuestionnaires,
    startNewConsultation,
    getPatientQuestionnairesByPatientyId,
  };
}
